import { Alert, Button, Card, Form, Input, Layout, Typography } from 'antd';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authService } from '../../services/auth';
import { getErrorMessage } from '../../utils/helpers';
import { DomainUtils } from '../../utils/domain';

const { Content } = Layout;
const { Title, Text } = Typography;

export const ResetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: any) => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await authService.resetPassword(token, values.password);
      setResetSuccess(true);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    const loginUrl = DomainUtils.getSubdomain() ? `${DomainUtils.getAppRootUrl()}/login` : '/login';

    if (loginUrl.startsWith('http')) {
      window.location.href = loginUrl;
    } else {
      navigate(loginUrl);
    }
  };

  if (resetSuccess) {
    return (
      <Content style={{ padding: '24px', maxWidth: 400, margin: '0 auto' }}>
        <Card>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
            Password Reset Successful
          </Title>

          <Alert
            title='Password Updated'
            description='Your password has been successfully reset. You can now log in with your new password.'
            type='success'
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Button type='primary' block onClick={handleLoginRedirect}>
            Go to Login
          </Button>
        </Card>
      </Content>
    );
  }

  if (!token) {
    return (
      <Content style={{ padding: '24px', maxWidth: 400, margin: '0 auto' }}>
        <Card>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
            Invalid Reset Link
          </Title>

          <Alert
            title='Invalid or Missing Token'
            description='This password reset link is invalid or has expired. Please request a new password reset.'
            type='error'
            showIcon
            style={{ marginBottom: 24 }}
          />

          <div style={{ textAlign: 'center' }}>
            <Link
              to={
                DomainUtils.getSubdomain()
                  ? `${DomainUtils.getAppRootUrl()}/forgot-password`
                  : '/forgot-password'
              }
            >
              <Button type='primary'>Request New Reset Link</Button>
            </Link>
          </div>
        </Card>
      </Content>
    );
  }

  return (
    <Content style={{ padding: '24px', maxWidth: 400, margin: '0 auto' }}>
      <Card>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
          Reset Password
        </Title>

        <Text type='secondary' style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
          Enter your new password below.
        </Text>

        {error && (
          <Alert
            title='Error'
            description={error}
            type='error'
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item
            name='password'
            label='New Password'
            rules={[
              { required: true, message: 'Please input your new password!' },
              { min: 8, message: 'Password must be at least 8 characters long!' },
            ]}
          >
            <Input.Password placeholder='Enter your new password' />
          </Form.Item>

          <Form.Item
            name='confirmPassword'
            label='Confirm New Password'
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder='Confirm your new password' />
          </Form.Item>

          <Form.Item>
            <Button type='primary' htmlType='submit' block loading={isLoading}>
              Reset Password
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Text type='secondary'>
            Remember your password?{' '}
            <Link
              to={DomainUtils.getSubdomain() ? `${DomainUtils.getAppRootUrl()}/login` : '/login'}
            >
              Back to Login
            </Link>
          </Text>
        </div>
      </Card>
    </Content>
  );
};
